import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { fetchAuthSession } from '@aws-amplify/auth';
import useTranslation from '../services/translationService';
import { apiPost } from '../utils/api';
import { isBreakglassModeActive } from '../services/breakglassService';
import useDashboardData from '../hooks/useDashboardData';
import useDashboardT from '../components/dashboard/useDashboardT';
import DashboardControls from '../components/dashboard/DashboardControls';
import DashboardSkeleton from '../components/dashboard/DashboardSkeleton';
import GgrHeroCard from '../components/dashboard/GgrHeroCard';
import NetworkBreakdownCard from '../components/dashboard/NetworkBreakdownCard';
import TopPlayersCard from '../components/dashboard/TopPlayersCard';
import NetworkSnapshotCard from '../components/dashboard/NetworkSnapshotCard';
import PlatformInsightsCard from '../components/dashboard/PlatformInsightsCard';
import { Card } from '../components/dashboard/ui';
import { describePeriod } from '../components/dashboard/periodText';

const PULL_THRESHOLD = 60;

const usePullToRefresh = (onRefresh) => {
  const [distance, setDistance] = useState(0);
  const [active, setActive] = useState(false);
  const startY = useRef(null);

  const onTouchStart = useCallback((event) => {
    startY.current = window.scrollY <= 0 ? event.touches[0].clientY : null;
  }, []);

  const onTouchMove = useCallback((event) => {
    if (startY.current === null || active) return;
    const delta = event.touches[0].clientY - startY.current;
    setDistance(delta > 0 ? Math.min(delta * 0.4, 80) : 0);
  }, [active]);

  const onTouchEnd = useCallback(async () => {
    if (startY.current === null) return;
    startY.current = null;
    if (distance < PULL_THRESHOLD) {
      setDistance(0);
      return;
    }
    setActive(true);
    try {
      await onRefresh();
    } finally {
      setActive(false);
      setDistance(0);
    }
  }, [distance, onRefresh]);

  return { distance, active, handlers: { onTouchStart, onTouchMove, onTouchEnd } };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t: translate, language } = useTranslation();
  const t = useDashboardT();
  const {
    tree,
    treeStatus,
    currency,
    setCurrency,
    period,
    setPeriod,
    windows,
    analytics,
    metrics,
    players,
    loadPlayerTotals,
    playerCount,
    network,
    platform,
    refresh,
    refreshing
  } = useDashboardData();
  const [breakglass, setBreakglass] = useState(false);
  const pull = usePullToRefresh(refresh);

  useEffect(() => {
    isBreakglassModeActive().then(setBreakglass).catch(() => {});
  }, []);

  useEffect(() => {
    // The dashboard is the landing page after sign-in; this records the user's IP and last login
    fetchAuthSession()
      .then(({ tokens }) => apiPost('coreAgent', '/core/agent', { action: 'initialLogin' }, tokens?.idToken?.toString()))
      .catch((error) => console.warn('Failed to record initial login:', error));
  }, []);

  if (breakglass) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <Card className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{t('breakglassMode', 'Access Restricted')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('breakglassModeDescription', 'Dashboard access is temporarily restricted for security reasons.')}
          </p>
        </Card>
      </div>
    );
  }

  const text = describePeriod(t, windows);
  const busy = refreshing || pull.active || analytics.status === 'loading' || analytics.status === 'refreshing';
  const analyticsFailed = analytics.status === 'error' && !metrics;
  const children = tree?.children || [];
  const hasNetwork = children.length > 0;
  const reportsArePlayers = hasNetwork && children.every((child) => child.type === 'player');
  const showTopPlayers = Boolean(tree) && tree.type !== 'player' && !reportsArePlayers && playerCount > 0 && !analyticsFailed;
  const openReports = (user) => navigate('/reports', { state: { selectedUser: { username: user.username, name: user.name } } });

  let content;
  if (!tree && treeStatus === 'error') {
    content = (
      <Card className="mx-auto mt-6 max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/60">
          <ExclamationTriangleIcon className="h-6 w-6 text-gray-400" />
        </div>
        <p className="font-medium text-gray-800 dark:text-gray-200">{t('unableToLoad', 'Unable to load your dashboard')}</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('unableToLoadHint', 'Check your connection and try again, or sign in again.')}</p>
        <div className="mt-5 flex justify-center gap-2">
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg bg-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            {t('retry', 'Try again')}
          </button>
          <Link
            to="/login"
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {t('signInAgain', 'Sign in')}
          </Link>
        </div>
      </Card>
    );
  } else if (!tree) {
    content = <DashboardSkeleton />;
  } else {
    content = (
      <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-12">
        <GgrHeroCard
          className="md:col-span-2 lg:col-span-12"
          t={t}
          language={language}
          currency={currency}
          windows={windows}
          text={text}
          metrics={metrics}
          players={players}
          status={analytics.status}
          error={analytics.error}
          fetchedAt={analytics.fetchedAt}
          onRefresh={refresh}
          busy={busy}
        />
        {hasNetwork && !analyticsFailed && (
          <NetworkBreakdownCard
            className={showTopPlayers ? 'lg:col-span-7' : 'md:col-span-2 lg:col-span-12'}
            t={t}
            currency={currency}
            rows={metrics?.breakdown}
            periodName={text.name}
            onSelect={openReports}
          />
        )}
        {showTopPlayers && (
          <TopPlayersCard
            className="lg:col-span-5"
            t={t}
            currency={currency}
            players={players}
            playerCount={playerCount}
            periodName={text.name}
            onLoad={loadPlayerTotals}
            onSelect={openReports}
          />
        )}
        {hasNetwork && (
          <NetworkSnapshotCard
            className="md:col-span-2 lg:col-span-12"
            t={t}
            currency={currency}
            network={network}
            closeLabel={translate('common.close')}
          />
        )}
        {platform.visible && (
          <PlatformInsightsCard
            className="md:col-span-2 lg:col-span-12"
            t={t}
            currency={currency}
            platform={platform}
            periodName={text.name}
            isLive={windows.isLive}
          />
        )}
        <div className="py-2 text-center md:col-span-2 lg:col-span-12">
          <Link
            to="/reports"
            className="group inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
          >
            {t('openReports', 'Open full reports')}
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl pb-[calc(2.5rem+env(safe-area-inset-bottom))]" {...pull.handlers}>
      <DashboardControls
        t={t}
        period={period}
        onPeriodChange={setPeriod}
        currency={currency}
        onCurrencyChange={setCurrency}
      />

      {(pull.distance > 0 || pull.active) && (
        <div
          className="flex items-center justify-center overflow-hidden transition-[height] duration-200"
          style={{ height: pull.active ? 44 : pull.distance }}
          aria-live="polite"
        >
          <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            <ArrowPathIcon
              className={`h-4 w-4 text-teal-500 transition-transform ${pull.distance >= PULL_THRESHOLD ? 'rotate-180' : ''} ${pull.active ? 'animate-spin motion-reduce:animate-none' : ''}`}
            />
            {pull.active
              ? t('refreshing', 'Refreshing…')
              : pull.distance >= PULL_THRESHOLD ? t('releaseToRefresh', 'Release to refresh') : t('pullToRefresh', 'Pull to refresh')}
          </span>
        </div>
      )}

      {content}
    </div>
  );
};

export default Dashboard;
